/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { useReactiveVar } from '@apollo/client';
import { DropdownMenuItemType, IDropdownOption } from '@fluentui/react/lib/Dropdown';
import * as Teams from '@microsoft/teams-js';
import * as React from 'react';
import {
    currentScopeInListVar,
    modalDataVar,
    ModalType,
    TemplateManagementListScope,
    TemplateScope,
} from '../../../apollo/reactiveVariables/TemplateManagementVariables';
import { LogCategory, PanelRegion, PanelType, ScenarioName, StatusName, StepName, SubWorkLoad } from '../../../logger';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { useIsAdmin } from '../../../utilities/TemplateManagementUtils';
import { TemplateManagementCommandBarComponent } from './TemplateManagementCommandBar.component';

export const TemplateManagementCommandBarContainer: React.FC = (): JSX.Element => {
    const host = getAppHost();
    const logger = host.getLogger();
    const [scopeList, setScopeList] = React.useState<any>();
    const currentScope = useReactiveVar(currentScopeInListVar);
    const isAdmin = useIsAdmin();

    React.useEffect((): void => {
        const setCommandBarData = async (): Promise<void> => {
            const startTime = performance.now();
            const host = getAppHost();
            const logger = host.getLogger();
            const loc = host.getLocale();
            const teamsInfo = await host.getUserOwnedTeams();
            const resultList: IDropdownOption[] = [];
            if (isAdmin) {
                // Add following tenant scopes as options if current user is admin
                resultList.push(
                    {
                        key: 'CompanyGroup',
                        text: loc.strings.company_group_text,
                        itemType: DropdownMenuItemType.Header,
                    },
                    {
                        key: TemplateScope.TenantAll,
                        text: loc.strings.org_wide_option_text,
                        data: { scope: TemplateScope.TenantAll },
                        ariaLabel: loc.strings.org_wide_option_text,
                    },
                    {
                        key: TemplateScope.TenantSpecificPeople,
                        text: loc.strings.specific_people_option_text,
                        data: { scope: TemplateScope.TenantSpecificPeople },
                        ariaLabel: loc.strings.specific_people_option_text,
                    }
                );
            }

            if (teamsInfo && teamsInfo.length > 0) {
                if (resultList.length > 0) {
                    resultList.push({
                        key: 'divider',
                        text: '-',
                        itemType: DropdownMenuItemType.Divider,
                    });
                }

                resultList.push({
                    key: 'TeamsGroup',
                    text: loc.strings.team_group_text,
                    itemType: DropdownMenuItemType.Header,
                });
                teamsInfo.forEach((teamInfo: Teams.TeamInformation): void => {
                    resultList.push({
                        key: teamInfo.groupId,
                        text: teamInfo.teamName,
                        data: { scope: TemplateScope.Team, team: { id: teamInfo.groupId, name: teamInfo.teamName } },
                        ariaLabel: teamInfo.teamName,
                    });
                });
            }

            setScopeList(resultList);
            if (currentScope.scope === TemplateScope.None) {
                // Set default scope to the key of first dropdown option item
                const scopeData = resultList?.[1]?.data as TemplateManagementListScope;
                logger.logUserBiAction(
                    SubWorkLoad.AppInteraction,
                    ScenarioName.TemplateManagement,
                    StepName.TemplateInstanceScopeSelected,
                    'click',
                    'none',
                    'templateInstanceListScope',
                    'templateInstanceListScopeDropdown',
                    PanelRegion.Main,
                    PanelType.TemplateManagement,
                    {
                        Scope: scopeData?.scope,
                    }
                );

                currentScopeInListVar(scopeData || { scope: TemplateScope.None });
            }

            logger.markScenario(
                ScenarioName.TemplateManagement,
                StepName.LoadTemplateInstanceScope,
                StatusName.Success,
                {
                    stepElapsed: performance.now() - startTime,
                }
            );
        };

        setCommandBarData();
    }, [setScopeList, isAdmin, currentScope.scope]);

    return (
        <TemplateManagementCommandBarComponent
            onScopeSelected={(scope: IDropdownOption): void => {
                const scopeData = scope.data as TemplateManagementListScope;
                logger.logTraceInfo(
                    LogCategory.TemplateManagement,
                    `${scopeData.scope} in scope dropdown is selected.`
                );

                logger.markScenario(
                    ScenarioName.TemplateManagement,
                    StepName.TemplateInstanceScopeSelected,
                    StatusName.NA,
                    {
                        data: {
                            Scope: scopeData.scope,
                            TeamId: scopeData.team?.id,
                        },
                    }
                );

                logger.logUserBiAction(
                    SubWorkLoad.AppInteraction,
                    ScenarioName.TemplateManagement,
                    StepName.TemplateInstanceScopeSelected,
                    'click',
                    'none',
                    'templateInstanceListScope',
                    'templateInstanceListScopeDropdown',
                    PanelRegion.Main,
                    PanelType.TemplateManagement,
                    {
                        Scope: scopeData.scope,
                        TeamId: scopeData.team?.id,
                    }
                );

                currentScopeInListVar(scopeData);
            }}
            selectedScope={currentScope.scope === TemplateScope.Team ? currentScope.team.id : currentScope.scope}
            scopeList={scopeList}
            onCreateTemplateClick={(): void => {
                logger.logTraceInfo(LogCategory.TemplateManagement, 'Create template button is clicked.');
                logger.markScenario(
                    ScenarioName.TemplateManagement,
                    StepName.OpenPreSetupTemplateInstanceModule,
                    StatusName.NA
                );
                logger.logUserBiAction(
                    SubWorkLoad.AppInteraction,
                    ScenarioName.TemplateManagement,
                    StepName.OpenPreSetupTemplateInstanceModule,
                    'click',
                    'nav',
                    'newTemplateInstance',
                    'newTemplateInstanceButton',
                    PanelRegion.Main,
                    PanelType.TemplateManagement
                );

                modalDataVar({ isOpen: true, type: ModalType.PreSetupWizard });
            }}
        />
    );
};
